import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { bkAdmin, nok } from "@/lib/booking";

const COST = [
  ["wage", "Timelønn"], ["holiday_pct", "Feriepenger %"], ["aga_pct", "Arbeidsgiveravgift %"], ["pension_pct", "Pensjon (OTP) %"],
  ["travel_min", "Reisetid per jobb (min)"], ["km", "Kjøring per jobb (km)"], ["km_rate", "Kr per km"], ["supplies", "Produkter/utstyr per jobb"],
  ["fee_pct", "Betalingsgebyr % (kort)"], ["admin_per_job", "Administrasjon per jobb"], ["target_margin_pct", "Ønsket margin %"],
];

export const ProfitCalc = () => {
  const [c, setC] = useState(null);
  const [job, setJob] = useState({ price: 1800, hours: 3, other: 0, card: true });
  useEffect(() => { bkAdmin.settings().then((s) => setC(s.costs)); }, []);
  if (!c) return null;
  const paid = (job.hours + c.travel_min / 60) * c.wage;
  const holiday = paid * c.holiday_pct / 100;
  const pension = (paid + holiday) * c.pension_pct / 100;
  const aga = (paid + holiday + pension) * c.aga_pct / 100;
  const labor = paid + holiday + pension + aga;
  const costs = labor + c.km * c.km_rate + c.supplies + c.admin_per_job + (job.card ? job.price * c.fee_pct / 100 : 0) + job.other;
  const margin = job.price - costs;
  const pct = job.price ? (margin / job.price) * 100 : 0;
  const minPrice = costs / Math.max(0.01, 1 - c.target_margin_pct / 100);
  const bad = margin < 0 || pct < c.target_margin_pct;
  const save = async () => { await bkAdmin.saveSettings({ costs: c }); toast.success("Kostnader lagret – brukes på alle oppdrag og avtaler"); };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]" data-testid="profit-calc">
      <div className="space-y-4">
        <div className="rounded-xl border bg-card p-4">
          <h3 className="font-heading font-bold mb-3">Kostnadsforutsetninger (lagres)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {COST.map(([k, l]) => <label key={k} className="text-xs">{l}<Input data-testid={`profit-${k}`} type="number" className="h-8 mt-1" value={c[k]} onChange={(e) => setC({ ...c, [k]: Number(e.target.value) })} /></label>)}
          </div>
          <Button size="sm" className="mt-3" data-testid="profit-save" onClick={save}>Lagre kostnader</Button>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <h3 className="font-heading font-bold mb-3">Test et oppdrag</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <label>Pris eks. mva<Input data-testid="profit-price" type="number" className="h-8 mt-1" value={job.price} onChange={(e) => setJob({ ...job, price: Number(e.target.value) })} /></label>
            <label>Arbeidstimer<Input data-testid="profit-hours" type="number" step="0.5" className="h-8 mt-1" value={job.hours} onChange={(e) => setJob({ ...job, hours: Number(e.target.value) })} /></label>
            <label>Andre kostnader<Input type="number" className="h-8 mt-1" value={job.other} onChange={(e) => setJob({ ...job, other: Number(e.target.value) })} /></label>
            <label className="flex items-center gap-1.5 pt-5"><input type="checkbox" checked={job.card} onChange={(e) => setJob({ ...job, card: e.target.checked })} />Kortbetaling</label>
          </div>
        </div>
      </div>
      <div className="rounded-xl border bg-card p-4 text-sm space-y-1.5 h-fit">
        <div className="flex justify-between"><span>Omsetning</span><b>{nok(job.price)}</b></div>
        <div className="flex justify-between"><span>Lønn inkl. ferie, pensjon, AGA</span><b>{nok(labor)}</b></div>
        <div className="flex justify-between"><span>Alle kostnader</span><b>{nok(costs)}</b></div>
        <div className="flex justify-between border-t pt-2 text-base"><span>Dekningsbidrag</span><b data-testid="profit-margin" className={bad ? "text-red-600" : "text-green-700"}>{nok(margin)} ({pct.toFixed(0)} %)</b></div>
        <div className="flex justify-between text-xs text-muted-foreground"><span>Pris for ønsket margin</span><span>{nok(minPrice)}</span></div>
        <div className={`mt-3 flex gap-2 rounded-lg p-3 ${bad ? "bg-red-50 text-red-700" : "bg-green-50 text-green-800"}`} data-testid="profit-verdict">
          {bad ? <AlertTriangle className="h-4 w-4 shrink-0" /> : <CheckCircle2 className="h-4 w-4 shrink-0" />}
          {margin < 0 ? "Oppdraget er ulønnsomt med disse tallene." : bad ? "Under ønsket margin – vurder høyere pris eller minstepris." : "Oppdraget ser lønnsomt ut."}
        </div>
        <p className="text-xs text-muted-foreground pt-2">Kun internt. Sjekk gjeldende minstelønn (allmenngjort tariff for renhold) og AGA-sone. Prisene senkes aldri automatisk.</p>
      </div>
    </div>
  );
};
